import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { showOpenDialog, readFile, saveFile } from './fileUtils.js';

const Team = ({ projectId }) => {
  const [teamAvailability, setTeamAvailability] = useState([]);
  const [artisticTeam, setArtisticTeam] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [newAvailability, setNewAvailability] = useState({
    teamMitglied: '',
    datum: '',
    anfangszeit: '',
    endzeit: '',
    bemerkungen: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'datum', direction: 'ascending' });
  const [rehearsalData, setRehearsalData] = useState([]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('.')) return dateStr;
    
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('-')) return dateStr;
    
    const parts = dateStr.split('.');
    if (parts.length !== 3) return dateStr;
    
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const sortByDate = (data) => {
    return [...data].sort((a, b) => {
      const dateA = parseDate(a.datum);
      const dateB = parseDate(b.datum);
      
      return new Date(dateA) - new Date(dateB);
    });
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!sortConfig.key) return teamAvailability;
    
    const sortableItems = [...teamAvailability];
    sortableItems.sort((a, b) => {
      if (sortConfig.key === 'datum') {
        const dateA = parseDate(a.datum);
        const dateB = parseDate(b.datum);
        
        if (sortConfig.direction === 'ascending') {
          return new Date(dateA) - new Date(dateB);
        } else {
          return new Date(dateB) - new Date(dateA);
        }
      }
      
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableItems;
  };

  useEffect(() => {
    const loadSavedData = () => {
      if (projectId) {
        const savedAvailability = localStorage.getItem(`teamAvailability_${projectId}`);
        if (savedAvailability) {
          try {
            const parsedAvailability = JSON.parse(savedAvailability);
            if (parsedAvailability && parsedAvailability.length > 0) {
              setTeamAvailability(parsedAvailability);
            }
          } catch (error) {
            console.error('Error loading team availability:', error);
          }
        }

        const savedArtisticTeam = localStorage.getItem(`artisticTeam_${projectId}`);
        if (savedArtisticTeam) {
          try {
            const parsedArtisticTeam = JSON.parse(savedArtisticTeam);
            if (parsedArtisticTeam && parsedArtisticTeam.length > 0) {
              setArtisticTeam(parsedArtisticTeam);
            }
          } catch (error) {
            console.error('Error loading artistic team:', error);
          }
        }
      }
    };

    loadSavedData();
  }, [projectId]);

  useEffect(() => {
    if (projectId && teamAvailability && teamAvailability.length > 0) {
      localStorage.setItem(`teamAvailability_${projectId}`, JSON.stringify(teamAvailability));
    }
  }, [teamAvailability, projectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAvailability(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCellEdit = (id, field, value) => {
    setTeamAvailability(prev => 
      prev.map(entry => 
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
    setEditingCell(null);
  };

  const saveAvailability = () => {
    if (newAvailability.teamMitglied && newAvailability.datum) {
      const availabilityToAdd = { 
        ...newAvailability,
        id: Date.now(),
        datum: formatDate(newAvailability.datum)
      };
      
      setTeamAvailability(prev => [...prev, availabilityToAdd]);
      
      setNewAvailability({
        teamMitglied: '',
        datum: '',
        anfangszeit: '',
        endzeit: '',
        bemerkungen: ''
      });
    }
  };

  const deleteAvailability = (id) => {
    if (window.confirm('Möchten Sie diesen Verfügbarkeitseintrag wirklich löschen?')) {
      setTeamAvailability(prev => {
        const updatedAvailability = prev.filter(entry => entry.id !== id);
  
        if (updatedAvailability.length === 0) {
          localStorage.removeItem(`teamAvailability_${projectId}`);
        } else {
          localStorage.setItem(`teamAvailability_${projectId}`, JSON.stringify(updatedAvailability));
        }
  
        return updatedAvailability;
      });
    }
  };

  const exportToCSV = () => {
    const csv = Papa.unparse(teamAvailability);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'team_verfuegbarkeit.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let data;
      if (file.name.endsWith('.csv')) {
        const text = await file.text();
        const results = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        data = results.data;
      } else {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, {
          type: 'array',
          dateNOS: true,
          cellDates: true
        });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        data = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          dateNF: 'YYYY-MM-DD'
        });
      }

      const newEntries = data
        .filter(entry => (entry.teamMitglied || entry.TeamMitglied) && 
                          (entry.datum || entry.Datum))
        .map(entry => {
          let datum = entry.datum || entry.Datum || '';
          
          if (datum.includes('-')) {
            datum = formatDate(datum);
          }
          
          return {
            id: Date.now() + Math.random(),
            teamMitglied: entry.teamMitglied || entry.TeamMitglied || '',
            datum: datum,
            anfangszeit: entry.anfangszeit || entry.Anfangszeit || '',
            endzeit: entry.endzeit || entry.Endzeit || '',
            bemerkungen: entry.bemerkungen || entry.Bemerkungen || ''
          };
        });

      setTeamAvailability(prev => [...prev, ...newEntries]);
      alert(`${newEntries.length} Teamverfügbarkeiten wurden erfolgreich importiert.`);

    } catch (error) {
      console.error('Fehler beim Datei-Import:', error);
      alert('Fehler beim Import der Datei. Bitte überprüfen Sie das Format.');
    }
    e.target.value = '';
  };

  const sortedTeamAvailability = getSortedData();

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Team</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <select
          name="teamMitglied"
          value={newAvailability.teamMitglied}
          onChange={handleInputChange}
          className="border p-2"
        >
          <option value="">Teammitglied auswählen</option>
          {artisticTeam.map((member) => (
            <option key={member.id} value={member.name}>
              {member.name}
            </option>
          ))}
        </select>

        <input
          type="date"
          name="datum"
          value={newAvailability.datum}
          onChange={handleInputChange}
          placeholder="Datum"
          className="border p-2"
        />

        <input
          type="time"
          name="anfangszeit"
          value={newAvailability.anfangszeit}
          onChange={handleInputChange}
          placeholder="Anfangszeit"
          className="border p-2"
        />

        <input
          type="time"
          name="endzeit"
          value={newAvailability.endzeit}
          onChange={handleInputChange}
          placeholder="Endzeit"
          className="border p-2"
        />

        <input
          type="text"
          name="bemerkungen"
          value={newAvailability.bemerkungen}
          onChange={handleInputChange}
          placeholder="Bemerkungen"
          className="border p-2"
        />

        <button
          onClick={saveAvailability}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
          disabled={!newAvailability.teamMitglied || !newAvailability.datum}
        >
          Verfügbarkeit speichern
        </button>
      </div>

      <div className="mb-4 flex space-x-2">
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
          className="hidden"
          id="csvUpload"
        />
        <label 
          htmlFor="csvUpload" 
          className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
        >
          Excel importieren
        </label>
        <button
          onClick={exportToCSV}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
        >
          Als Excel exportieren
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border">
          <thead>
            <tr className="bg-gray-200">
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('teamMitglied')}
              >
                Teammitglied {sortConfig.key === 'teamMitglied' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('datum')}
              >
                Datum {sortConfig.key === 'datum' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('anfangszeit')}
              >
                Anfangszeit {sortConfig.key === 'anfangszeit' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('endzeit')}
              >
                Endzeit {sortConfig.key === 'endzeit' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('bemerkungen')}
              >
                Bemerkungen {sortConfig.key === 'bemerkungen' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th className="border p-2">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {sortedTeamAvailability.map((entry) => (
              <tr key={entry.id}>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: entry.id, field: 'teamMitglied' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'teamMitglied' ? (
                    <select
                      value={entry.teamMitglied}
                      onChange={(e) => handleCellEdit(entry.id, 'teamMitglied', e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    >
                      {artisticTeam.map((member) => (
                        <option key={member.id} value={member.name}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  ) : entry.teamMitglied}
                </td>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: entry.id, field: 'datum' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'datum' ? (
                    <input
                      type="date"
                      value={parseDate(entry.datum)}
                      onChange={(e) => handleCellEdit(entry.id, 'datum', formatDate(e.target.value))}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    />
                  ) : entry.datum}
                </td>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: entry.id, field: 'anfangszeit' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'anfangszeit' ? (
                    <input
                      type="time"
                      value={entry.anfangszeit || ''}
                      onChange={(e) => handleCellEdit(entry.id, 'anfangszeit', e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    />
                  ) : entry.anfangszeit}
                </td>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: entry.id, field: 'endzeit' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'endzeit' ? (
                    <input
                      type="time"
                      value={entry.endzeit || ''}
                      onChange={(e) => handleCellEdit(entry.id, 'endzeit', e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    />
                  ) : entry.endzeit}
                </td>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: entry.id, field: 'bemerkungen' })}
                >
                  {editingCell?.id === entry.id && editingCell?.field === 'bemerkungen' ? (
                    <input
                      type="text"
                      value={entry.bemerkungen || ''}
                      onChange={(e) => handleCellEdit(entry.id, 'bemerkungen', e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    />
                  ) : entry.bemerkungen}
                </td>
                <td className="border p-2">
                  <button
                    onClick={() => deleteAvailability(entry.id)}
                    className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Team;
